import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Package, X, Printer, ArrowRight, Clock, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, Volume2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => n != null ? Number(n).toLocaleString("en-IN") : "0";

// Notification sound
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Two-tone chime
    [440, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.4);
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 0.4);
    });
  } catch (e) { /* silent */ }
};

// Hover tooltip for order details
const OrderHoverTooltip = ({ order, alert, children }) => {
  const [show, setShow] = useState(false);
  const allocation = (alert.allocations || []).find(a => a.order_id === order.order_id);
  const qtyAllocated = allocation ? allocation.qty_allocated : 0;
  const qtyStillPending = order.qty_pending - qtyAllocated;

  return (
    <div className="relative" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div className="absolute bottom-full left-0 mb-1 z-50 bg-gray-900 text-white rounded-lg shadow-xl p-3 text-xs w-64 pointer-events-none">
          <p className="font-bold text-yellow-300 mb-1">{order.order_id}</p>
          <p className="text-gray-300">{order.customer_name}</p>
          <div className="mt-2 space-y-1 border-t border-gray-700 pt-2">
            <div className="flex justify-between">
              <span className="text-gray-400">Qty Ordered:</span>
              <span className="font-semibold">{fmt(order.qty_ordered)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Qty Pending:</span>
              <span className="font-semibold text-orange-300">{fmt(order.qty_pending)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Allocated from this:</span>
              <span className={`font-semibold ${qtyAllocated > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                {qtyAllocated > 0 ? fmt(qtyAllocated) : 'Not yet'}
              </span>
            </div>
            {qtyStillPending > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">Still short:</span>
                <span className="font-semibold text-red-400">{fmt(qtyStillPending)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 mt-2 text-gray-400">
            <Clock className="w-3 h-3" />
            <span>Waiting {order.days_waiting} days</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Print function
const printAlertDetails = (alert) => {
  const pendingRows = (alert.pending_orders || []).map((o, i) => {
    const alloc = (alert.allocations || []).find(a => a.order_id === o.order_id);
    return `<tr>
      <td style="padding:6px;border:1px solid #ddd">${i + 1}</td>
      <td style="padding:6px;border:1px solid #ddd">${o.order_id}</td>
      <td style="padding:6px;border:1px solid #ddd">${o.customer_name}</td>
      <td style="padding:6px;border:1px solid #ddd;text-align:right">${o.qty_pending}</td>
      <td style="padding:6px;border:1px solid #ddd;text-align:right;color:${alloc ? '#16a34a' : '#dc2626'}">${alloc ? alloc.qty_allocated : '-'}</td>
      <td style="padding:6px;border:1px solid #ddd;text-align:right">${o.days_waiting}d</td>
      <td style="padding:6px;border:1px solid #ddd">${o.order_date?.slice(0, 10) || ''}</td>
    </tr>`;
  }).join('');

  const allocRows = (alert.allocations || []).map((a, i) =>
    `<tr>
      <td style="padding:6px;border:1px solid #ddd">${i + 1}</td>
      <td style="padding:6px;border:1px solid #ddd">${a.order_id}</td>
      <td style="padding:6px;border:1px solid #ddd">${a.customer_name}</td>
      <td style="padding:6px;border:1px solid #ddd;text-align:right;font-weight:bold;color:#16a34a">${a.qty_allocated}</td>
      <td style="padding:6px;border:1px solid #ddd">${a.allocated_by === 'system_auto' ? 'Auto' : a.allocated_by}</td>
    </tr>`
  ).join('');

  const statusLabel = alert.allocation_status === 'auto_allocated' ? 'AUTO-ALLOCATED'
    : alert.allocation_status === 'manually_allocated' ? 'MANUALLY ALLOCATED'
    : alert.allocation_status === 'partial' ? 'PARTIALLY ALLOCATED'
    : 'NEEDS ALLOCATION';

  const html = `<!DOCTYPE html><html><head><title>NTO Received - ${alert.part_no}</title>
    <style>body{font-family:Arial,sans-serif;margin:20px;font-size:13px}
    table{border-collapse:collapse;width:100%}
    th{background:#f3f4f6;padding:8px;border:1px solid #ddd;text-align:left;font-size:12px}
    .header{border-bottom:3px solid #2563eb;padding-bottom:10px;margin-bottom:15px}
    .badge{display:inline-block;padding:3px 10px;border-radius:12px;font-size:11px;font-weight:bold}
    .received{background:#dcfce7;color:#166534}.pending{background:#fef3c7;color:#92400e}
    @media print{body{margin:10px}}</style></head><body>
    <div class="header">
      <h2 style="margin:0;color:#1e40af">NTO Item Received</h2>
      <p style="color:#6b7280;margin:4px 0">Jai Paras Spares - ${new Date().toLocaleDateString('en-IN')}</p>
    </div>
    <table style="margin-bottom:20px">
      <tr><th style="width:140px">Part Number</th><td style="padding:6px;border:1px solid #ddd;font-weight:bold;font-size:15px">${alert.part_no}</td></tr>
      <tr><th>Part Name</th><td style="padding:6px;border:1px solid #ddd">${alert.part_name}</td></tr>
      <tr><th>Qty Received</th><td style="padding:6px;border:1px solid #ddd;font-weight:bold;font-size:15px;color:#16a34a">${alert.qty_received}</td></tr>
      <tr><th>Supplier</th><td style="padding:6px;border:1px solid #ddd">${alert.supplier || '-'}</td></tr>
      <tr><th>Godown</th><td style="padding:6px;border:1px solid #ddd">${alert.godown_name || '-'}</td></tr>
      <tr><th>Received On</th><td style="padding:6px;border:1px solid #ddd">${alert.received_at?.slice(0, 10) || ''}</td></tr>
      <tr><th>Status</th><td style="padding:6px;border:1px solid #ddd"><span class="badge ${alert.allocation_status === 'auto_allocated' || alert.allocation_status === 'manually_allocated' ? 'received' : 'pending'}">${statusLabel}</span></td></tr>
      <tr><th>Total Pending Qty</th><td style="padding:6px;border:1px solid #ddd;font-weight:bold;color:#ea580c">${alert.total_pending_qty}</td></tr>
    </table>
    <h3 style="color:#1e40af;border-bottom:2px solid #93c5fd;padding-bottom:5px">Pending Orders (${(alert.pending_orders || []).length})</h3>
    <table><thead><tr><th>#</th><th>Order ID</th><th>Customer</th><th>Qty Pending</th><th>Allocated</th><th>Waiting</th><th>Order Date</th></tr></thead>
    <tbody>${pendingRows || '<tr><td colspan="7" style="padding:10px;text-align:center;color:#9ca3af">No pending orders</td></tr>'}</tbody></table>
    ${allocRows ? `<h3 style="color:#16a34a;border-bottom:2px solid #86efac;padding-bottom:5px;margin-top:20px">Allocations (${(alert.allocations || []).length})</h3>
    <table><thead><tr><th>#</th><th>Order ID</th><th>Customer</th><th>Qty Allocated</th><th>By</th></tr></thead>
    <tbody>${allocRows}</tbody></table>` : ''}
    <p style="margin-top:20px;color:#9ca3af;font-size:11px;text-align:center">Printed from Jai Paras WMS</p>
    </body></html>`;

  const w = window.open('', '_blank', 'width=800,height=600');
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
};

// Single alert card
const AlertCard = ({ alert, token, onDismiss, onAllocate }) => {
  const [expanded, setExpanded] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [allocQtys, setAllocQtys] = useState({});
  const pendingOrders = alert.pending_orders || [];
  const isAutoAllocated = alert.allocation_status === 'auto_allocated';
  const needsManual = alert.allocation_status === 'needs_manual';
  const isShort = alert.qty_received < alert.total_pending_qty;
  const headers = { Authorization: `Bearer ${token}` };

  const handleAllocate = async () => {
    setAllocating(true);
    try {
      const allocations = Object.entries(allocQtys)
        .filter(([, qty]) => qty > 0)
        .map(([orderId, qty]) => ({ order_id: orderId, qty_allocated: parseInt(qty) }));
      if (allocations.length === 0) { setAllocating(false); return; }
      await axios.post(`${API}/nto-received/alerts/${alert.id}/allocate`, { allocations }, { headers });
      onAllocate?.();
    } catch (e) {
      console.error('Allocation failed', e);
    }
    setAllocating(false);
  };

  const statusColor = isAutoAllocated ? 'bg-green-100 text-green-700 border-green-300'
    : needsManual ? 'bg-orange-100 text-orange-700 border-orange-300'
    : alert.allocation_status === 'manually_allocated' ? 'bg-blue-100 text-blue-700 border-blue-300'
    : 'bg-gray-100 text-gray-600 border-gray-300';

  const statusLabel = isAutoAllocated ? 'Auto-Allocated'
    : needsManual ? 'Needs Allocation'
    : alert.allocation_status === 'manually_allocated' ? 'Allocated'
    : alert.allocation_status === 'partial' ? 'Partial'
    : 'Pending';

  return (
    <Card className={`border shadow-lg mb-2 animate-in slide-in-from-left-5 ${isShort ? 'border-l-4 border-l-orange-500' : 'border-l-4 border-l-green-500'}`} data-testid={`nto-alert-${alert.id}`}>
      <CardContent className="p-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Package className="w-4 h-4 text-blue-600 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-bold text-gray-900 truncate">{alert.part_no}</p>
              <p className="text-[10px] text-gray-500 truncate">{alert.part_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Badge className={`text-[9px] ${statusColor}`}>{statusLabel}</Badge>
            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => printAlertDetails(alert)} data-testid={`nto-alert-print-${alert.id}`}>
              <Printer className="w-3 h-3 text-gray-400" />
            </Button>
            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onDismiss(alert.id)} data-testid={`nto-alert-dismiss-${alert.id}`}>
              <X className="w-3 h-3 text-gray-400" />
            </Button>
          </div>
        </div>

        {/* Key info */}
        <div className="flex items-center gap-3 mt-2 text-[10px]">
          <span className="font-bold text-green-700">Received: {alert.qty_received}</span>
          <span className="text-orange-600">Pending: {alert.total_pending_qty}</span>
          {alert.supplier && <span className="text-gray-400">From: {alert.supplier}</span>}
          <span className="text-gray-400">{pendingOrders.length} order{pendingOrders.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Fulfillment status */}
        {isShort && (
          <div className="mt-1 px-2 py-1 bg-orange-50 rounded text-[10px] text-orange-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Short by {alert.total_pending_qty - alert.qty_received} — manual allocation needed
          </div>
        )}
        {isAutoAllocated && (
          <div className="mt-1 px-2 py-1 bg-green-50 rounded text-[10px] text-green-700 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Sufficient qty — auto-allocated to all {pendingOrders.length} orders
          </div>
        )}

        {/* Expandable pending orders */}
        <Button variant="ghost" size="sm" className="w-full mt-1 h-6 text-[10px] text-gray-500" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
          {expanded ? 'Hide' : 'Show'} {pendingOrders.length} pending orders
        </Button>

        {expanded && (
          <div className="mt-1 space-y-1 max-h-48 overflow-y-auto">
            {pendingOrders.map((order, idx) => (
              <OrderHoverTooltip key={order.order_id} order={order} alert={alert}>
                <div className={`flex items-center justify-between p-1.5 rounded text-[10px] border ${order.days_waiting >= 7 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="min-w-0">
                    <span className="font-semibold text-blue-700">{order.order_id}</span>
                    <span className="text-gray-500 ml-1 truncate">{order.customer_name}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-orange-600">Pending: {order.qty_pending}</span>
                    <span className={`flex items-center gap-0.5 ${order.days_waiting >= 7 ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                      <Clock className="w-2.5 h-2.5" />{order.days_waiting}d
                    </span>
                    {needsManual && (
                      <input
                        type="number"
                        min="0"
                        max={Math.min(order.qty_pending, alert.qty_received)}
                        placeholder="Qty"
                        className="w-14 h-5 text-[10px] border rounded px-1 text-center"
                        value={allocQtys[order.order_id] || ''}
                        onChange={(e) => setAllocQtys(prev => ({ ...prev, [order.order_id]: e.target.value }))}
                        data-testid={`alloc-input-${order.order_id}`}
                      />
                    )}
                  </div>
                </div>
              </OrderHoverTooltip>
            ))}

            {needsManual && (
              <Button
                size="sm"
                className="w-full h-7 text-[10px] bg-blue-600 hover:bg-blue-700 mt-1"
                onClick={handleAllocate}
                disabled={allocating}
                data-testid="nto-allocate-btn"
              >
                {allocating ? 'Allocating...' : 'Allocate Stock'}
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main notification container
const NtoReceivedNotification = ({ token }) => {
  const [alerts, setAlerts] = useState([]);
  const prevCountRef = useRef(0);
  const pollRef = useRef(null);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/nto-received/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newAlerts = res.data?.alerts || [];
      // Play sound if new alerts arrived
      if (newAlerts.length > prevCountRef.current && prevCountRef.current !== null) {
        playNotificationSound();
      }
      prevCountRef.current = newAlerts.length;
      setAlerts(newAlerts);
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
    // Poll every 30 seconds
    pollRef.current = setInterval(fetchAlerts, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchAlerts]);

  const dismissAlert = async (alertId) => {
    try {
      await axios.post(`${API}/nto-received/alerts/${alertId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) { console.error('Dismiss failed', e); }
  };

  const dismissAll = async () => {
    if (!alerts.length) return;
    // Optimistic clear — backend dismissal is idempotent and per-user
    const snapshot = alerts;
    setAlerts([]);
    prevCountRef.current = 0;
    try {
      await axios.post(`${API}/nto-received/alerts/dismiss-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error('Dismiss-all failed', e);
      setAlerts(snapshot); // restore on failure
    }
  };

  if (alerts.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 w-80 max-h-[70vh] overflow-y-auto space-y-1" data-testid="nto-received-notifications">
      <div className="flex items-center justify-between px-1 mb-1">
        <div className="flex items-center gap-1">
          <Volume2 className="w-3 h-3 text-blue-500" />
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wide">NTO Items Received ({alerts.length})</span>
        </div>
        <button
          type="button"
          onClick={dismissAll}
          data-testid="nto-clear-all-btn"
          className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 hover:underline px-1.5 py-0.5 rounded transition"
          title="Dismiss all NTO alerts at once (per-user)"
        >
          Clear All
        </button>
      </div>
      {alerts.map(alert => (
        <AlertCard
          key={alert.id}
          alert={alert}
          token={token}
          onDismiss={dismissAlert}
          onAllocate={fetchAlerts}
        />
      ))}
    </div>
  );
};

export default NtoReceivedNotification;
